import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { QuotaService } from '../../../quotas/services/quota.service';
import { OepSignatureDialogComponent, OepSignatureDialogData } from '../../../../shared/components/oep-signature-dialog/oep-signature-dialog.component';

export interface QuotaStatusDialogData {
  quota: any;
}

@Component({
  selector: 'app-quota-status-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSnackBarModule,
    TranslateModule
  ],
  templateUrl: './quota-status-dialog.component.html',
  styleUrls: ['./quota-status-dialog.component.css']
})
export class QuotaStatusDialogComponent {
  private quotaService = inject(QuotaService);
  private dialog = inject(MatDialog);
  private dialogRef = inject(MatDialogRef<QuotaStatusDialogComponent>);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  data = inject<QuotaStatusDialogData>(MAT_DIALOG_DATA);

  loading = signal(false);
  errorMessage = signal('');
  fieldErrors = signal<Record<string, string>>({});

  organizationInn = '';
  personIdnp = '';
  status = 'ACTIVE';
  adminComment = '';

  getFieldError(field: string): string {
    return this.fieldErrors()[field] || '';
  }

  clearFieldError(field: string): void {
    const errors = { ...this.fieldErrors() };
    delete errors[field];
    this.fieldErrors.set(errors);
  }

  onKeyDown(event: KeyboardEvent): void {
    const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
    if (!allowed.includes(event.key) && !/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onNumericInput(event: Event, field: 'personIdnp' | 'organizationInn'): void {
    const input = event.target as HTMLInputElement;
    const filtered = input.value.replace(/\D/g, '').slice(0, 14);
    input.value = filtered;
    this[field] = filtered;
    this.clearFieldError(field);
  }

  isFormValid(): boolean {
    return !!(
      this.organizationInn.length === 14 &&
      this.personIdnp.length === 14 &&
      this.status &&
      this.adminComment
    );
  }

  submit(): void {
    if (!this.isFormValid()) return;

    this.errorMessage.set('');

    // Открываем диалог OEP подписи
    const signatureDialogRef = this.dialog.open(OepSignatureDialogComponent, {
      width: '400px',
      disableClose: true,
      data: {
        organizationInn: this.organizationInn,
        personIdnp: this.personIdnp,
        title: 'Подпись для изменения статуса'
      } as OepSignatureDialogData
    });

    signatureDialogRef.afterClosed().subscribe(result => {
      if (result?.success && result.token) {
        this.updateStatus(result.token);
      } else if (result?.fieldErrors) {
        this.fieldErrors.set(result.fieldErrors);
      }
    });
  }

  private updateStatus(token: string): void {
    this.loading.set(true);

    this.quotaService.updateQuotaStatus({
      adminComment: this.adminComment,
      organizationInn: this.organizationInn,
      personIdnp: this.personIdnp,
      quotaId: this.data.quota.id,
      status: this.status,
      token: token
    }).subscribe({
      next: (response) => {
        this.loading.set(false);
        if (response.success) {
          this.snackBar.open('Статус квоты успешно обновлен', 'OK', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top'
          });
          this.dialogRef.close({ success: true });
        } else {
          this.handleApiErrors(response);
        }
      },
      error: (error) => {
        console.error('Error updating quota status:', error);
        if (error.error && Array.isArray(error.error.res)) {
          this.handleApiErrors(error.error);
        } else {
          this.errorMessage.set('Ошибка обновления статуса');
        }
        this.loading.set(false);
      }
    });
  }

  private handleApiErrors(response: { msg?: string; res?: Array<{ field: string; ru: string }> }): void {
    if (response.res && Array.isArray(response.res)) {
      const errors: Record<string, string> = {};
      response.res.forEach(err => {
        errors[err.field] = err.ru;
      });
      this.fieldErrors.set(errors);

      if (response.res.length === 0 && response.msg) {
        this.errorMessage.set(response.msg);
      }
    } else if (response.msg) {
      this.errorMessage.set(response.msg);
    } else {
      this.errorMessage.set('Ошибка обновления статуса');
    }
  }

  close(): void {
    this.dialogRef.close({ success: false });
  }

  getMeatTypeName(meatType: any): string {
    if (!meatType) return '';
    const lang = this.translate.currentLang || 'ru';
    return lang === 'ky' ? meatType.nameKy : meatType.nameRu;
  }
}
